import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { CutLetters } from "./MiniPlayer";
import { askForLogin } from "../lib/askLogin";
import { askForPremium } from "../lib/askPremium";
import { FINISHES, loadSlab, saveFinish, useApplyFinish } from "../lib/finish";
import "./finishes.css";

// A small picture of each look, for the list.
const SWATCH = {
  steel: "radial-gradient(circle at 36% 28%, #ffffff 0%, #bcc4cc 40%, #6c7580 100%)",
  gold: "radial-gradient(circle at 36% 28%, #fffbe0 0%, #f7c928 40%, #8f6205 100%)",
  quartz: "radial-gradient(circle at 36% 28%, #ffffff 0%, #efe9dc 45%, #c9a13a 100%)",
};

// The "Dan The Man TV" plate in the top left corner. Pressing it opens the list of looks for everyone: Steel, Gold (a free
// login) and Quartz (Premium). The choice is kept on the device (lib/finish.js) and paints every steel piece on the site
// (components/finishes.css). A guest who presses Gold or Quartz, or a free member who presses Quartz, gets a 10 second
// sample of it (nothing is saved), then the "Create a free login" box or the Premium note, and the site goes back to the
// look they had. Below the wide-screen size the top menu has no Home link, so the list also has one.
const SAMPLE_MS = 10000;

export default function BrandPlate({ user, ready }) {
  const premium = !!(user && (user.is_premium || user.role === "admin"));
  // while the login is still loading, a device that has a login saved is treated as logged in, so the look does not flash back to steel
  let saved = false;
  try { saved = !!localStorage.getItem("dtm_token"); } catch { /* storage blocked */ }
  const [trying, setTrying] = useState(null); // the look being sampled for 10 seconds ("gold" or "quartz"), or null
  const timer = useRef(null);
  const { own } = useApplyFinish(!!user || (!ready && saved), premium || !ready, trying);
  const stopTrying = () => { clearTimeout(timer.current); timer.current = null; setTrying(null); };
  useEffect(() => () => clearTimeout(timer.current), []);
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);
  const { pathname } = useLocation();
  useEffect(() => { setOpen(false); }, [pathname, user]);
  // a login or a logout ends a sample
  useEffect(() => { stopTrying(); }, [user]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (open) loadSlab(); }, [open]); // get the Quartz photo ready, so a sample shows at once
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("pointerdown", onDown);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("pointerdown", onDown); document.removeEventListener("keydown", onKey); };
  }, [open]);

  const sub = <span className="hidden md:block lg:hidden 2xl:block font-mono-ui text-[10px] text-cyan-400 uppercase tracking-widest leading-none">AI Music • Videos • Entertainment</span>;
  const pick = (id) => {
    // what this person has to do first to keep the look: log in (Gold) or go Premium (Quartz)
    const needs = id === "quartz" ? (premium ? null : user ? "premium" : "login") : id === "gold" ? (user ? null : "login") : null;
    if (needs) {
      if (trying) return;
      setOpen(false); // close the list so they can see the site
      setTrying(id);
      timer.current = setTimeout(() => {
        timer.current = null;
        setTrying(null); // back to the look they had
        if (needs === "login") askForLogin("look");
        else askForPremium("That was a sample of the Quartz look. It is for Premium members.");
      }, SAMPLE_MS);
      return;
    }
    stopTrying();
    if (user) saveFinish(id); // a guest is always on steel, so there is nothing to keep
  };
  return (
    <div ref={boxRef} className="relative flex flex-col justify-center gap-1 shrink-0 min-w-0">
      <button type="button" data-testid="brand-finish-btn" aria-haspopup="menu" aria-expanded={open} title="Change the look of the buttons" onClick={() => setOpen((o) => !o)}
        className="flex shrink-0 text-left">
        <span className="steel-plate steel-pill"><CutLetters text="Dan The Man TV" /></span>
      </button>
      <Link to="/" data-testid="brand-link" tabIndex={-1} className="contents">{sub}</Link>
      {open && (
        <div role="menu" aria-label="Look of the buttons" data-testid="finish-menu"
          className="absolute left-0 top-full mt-2 z-50 w-60 rounded-xl border border-white/15 bg-zinc-900 p-2 shadow-2xl">
          <Link to="/" role="menuitem" data-testid="finish-home" className="lg:hidden flex items-center gap-3 px-3 py-2.5 min-h-[44px] rounded-lg text-sm font-semibold text-zinc-100 hover:bg-white/10">Home page</Link>
          <div className="px-3 pt-1.5 pb-1 text-[11px] font-mono-ui uppercase tracking-widest text-zinc-500">Look of the buttons</div>
          {FINISHES.map(([id, label]) => {
            const lock = id === "quartz" ? (premium ? null : "Premium") : id === "gold" && !user ? "Free login" : null;
            const on = own === id;
            return (
              <button type="button" key={id} role="menuitemradio" aria-checked={on} data-testid={`finish-${id}`} onClick={() => pick(id)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 min-h-[44px] rounded-lg text-left text-sm font-semibold hover:bg-white/10 ${on ? "bg-white/10 text-white" : "text-zinc-300"}`}>
                <span className="w-5 h-5 rounded-full shrink-0 border border-black/60" style={{ background: SWATCH[id] }} aria-hidden="true" />
                <span className="flex-1">{label}</span>
                {lock && (trying === id ? <span className="text-xs text-cyan-300">Trying it</span> : <span className="text-xs text-amber-300">{lock}</span>)}
                {on && <span className="text-cyan-300" aria-hidden="true">✓</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
